import { useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Bell,
  CaretRight,
  DotsThreeCircle,
  SignOut,
  X
} from "@phosphor-icons/react";
import { useSession } from "../auth/SessionProvider";
import { personName, usePortalIdentity } from "../auth/usePortalIdentity";
import { ADMIN_HOME } from "../auth/redirectTarget";
import { notificationsKey, useUnreadNotificationCount } from "../data/portalQueries";
import { useUnreadChatSummary } from "../data/overviewQueries";
import { useClientSiteFactsSites } from "../data/clientFactsQueries";
import { useClientSiteCounts } from "../data/statisticsQueries";
import { usePortalChannel } from "../realtime/usePortalChannel";
import { PortalOpening } from "./PortalOpening";
import { DESTINATIONS, MOBILE_PRIMARY, MOBILE_SECONDARY, withFacts, withStatistics } from "./destinations";
import { useThemePreference } from "./useTheme";
import { ConnectionNotice } from "./ConnectionNotice";
import { UpdatePrompt } from "../pwa/UpdatePrompt";
import { useKeyboardViewport } from "./useKeyboardViewport";

/**
 * The approved shell from design.md section 5.
 *
 * Six destinations on desktop in a left rail that always shows the current location. On mobile at
 * most five persistent items: the four most used, plus `Mer` for the rest. The active destination
 * is marked by weight, shape and contrast together, never colour alone.
 */

export /**
 * The two screens inside this shell that are about the person rather than about a client.
 *
 * Settings holds the second factor, the sessions and the device panel, and an admin needs all three.
 * The bell's destination is kept for the same reason. Every other screen here reads one client's
 * data and has nothing to show an account that belongs to no client.
 */
const PERSONAL_PATHS = ["/settings", "/notifications"];

/**
 * Whether a platform admin standing here belongs on Petrovic's own surface instead.
 *
 * Added 2026-09-23, round D. Mattej signed in on his iPhone from the home-screen icon and every view
 * but Inställningar said "Den här vyn är inte din" or "Ingen aktiv åtkomst", while the same account
 * worked on his computer. Staging showed nothing wrong with the account: an active admin row, one
 * verified factor, no membership, and the phone's session at `aal2` like the computer's. The fault
 * was the route. The installed app opens at the manifest's `start_url`, `/overview`, and so does
 * `/`. `RequireSession` remembers `/overview` as the destination, and the sign-in screen rightly
 * prefers a remembered destination over `ADMIN_HOME`. So an admin who started from the icon or the
 * bare domain was delivered into the client shell, whose seven destinations are one client's and
 * none of them his. On the computer he opened `/admin/clients` directly, which is why it worked there.
 * Screen width had nothing to do with it.
 *
 * Decided here, in the one layout every client screen shares, rather than in the sign-in screen,
 * because the icon is not the only way in. A bookmark, the bare domain and a stale tab land on a
 * client screen without passing `/login`. `isPlatformAdmin` is the server's own answer and requires
 * `aal2`, and an admin row with a membership would keep the client shell, so no client is ever
 * moved by this. Nothing is granted either way: the admin gateways refuse on their own.
 */
function belongsOnAdminSurface(
  identity: { isPlatformAdmin: boolean; tenantId: string | null } | undefined,
  pathname: string
): boolean {
  if (!identity?.isPlatformAdmin || identity.tenantId !== null) {
    return false;
  }

  return !PERSONAL_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/**
 * What the bell announces, including the case where it knows nothing.
 *
 * Singular and plural are separated because "1 olästa" is not Swedish, and this string is read out
 * loud by the screen readers that are the only reason the label exists.
 */
function notificationLabel(unread: number | null): string {
  if (unread === null) {
    return "Notiser";
  }
  if (unread === 0) {
    return "Notiser, inga olästa";
  }
  return unread === 1 ? "Notiser, 1 oläst" : `Notiser, ${unread} olästa`;
}

export function AppShell() {
  const { signOut, userId } = useSession();
  const identity = usePortalIdentity();
  const unreadCount = useUnreadNotificationCount();
  /*
   * The theme is chosen in Inställningar › Utseende since 2026-09-24, as design.md section 3 asks,
   * and the header toggle is gone. The shell still reads the stored choice on every load, so a person
   * who chose Mörkt gets it on the first screen and not only after visiting the settings.
   */
  useThemePreference();
  const [moreOpen, setMoreOpen] = useState(false);
  const [openingDone, setOpeningDone] = useState(() =>
    sessionStorage.getItem("portal-opening-shown") === "1"
  );
  const moreButton = useRef<HTMLButtonElement | null>(null);
  const moreSheet = useRef<HTMLElement | null>(null);
  const location = useLocation();

  // A private topic per user. Subscribed while the shell is mounted, dropped when it unmounts.
  usePortalChannel(userId ? { kind: "user", id: userId, channel: "notifications" } : null, {
    invalidate: [notificationsKey]
  });

  useEffect(() => {
    if (openingDone) {
      sessionStorage.setItem("portal-opening-shown", "1");
    }
  }, [openingDone]);

  /**
   * The two ways out of the sheet that every other dismissible surface in the portal already has.
   *
   * `Nytt ärende` and the other modals are `<dialog>` elements and get Escape from the browser. This
   * sheet is not a dialog, deliberately, because it is a navigation and not a modal: it does not
   * trap focus and the rest of the portal stays usable behind it. What it inherited from that choice
   * was having no keyboard exit at all. Until now the only way to close it was the `Mer` button
   * itself, so a person who opened it and reached for Escape, or tapped the page beside it, stayed
   * where they were with no way to tell why.
   *
   * `pointerdown` rather than `click`, so the sheet is gone before the press lands on whatever is
   * underneath it. The `Mer` button is excluded from "outside" because its own `click` toggles: with
   * the button counted as outside, a press on it would close the sheet here and reopen it a moment
   * later, and the control would look broken.
   *
   * Focus goes back to the button on Escape, because a keyboard user who dismisses something has to
   * land somewhere, and the place they were is the control they opened it from.
   */
  useEffect(() => {
    if (!moreOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
        moreButton.current?.focus();
      }
    };

    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }
      if (moreSheet.current?.contains(target) || moreButton.current?.contains(target)) {
        return;
      }
      setMoreOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [moreOpen]);

  /**
   * Null while the count is unknown, and null is not zero.
   *
   * The shell used to count unread rows inside the fifty `useNotifications` returns, which meant a
   * client with more than fifty notifications could have an unread one that fell off the end and a
   * bell that said "inga olästa". It now asks the server for an exact count and fetches no bodies at
   * all. When that question has not been answered the label makes no claim either way, because a
   * portal that says "inga olästa" on a failed request is stating a fact it does not have.
   */
  const unread = unreadCount.data ?? null;
  const person = personName(identity);

  /*
   * Unread chat messages are a count on Chatt in the navigation and nowhere else, since build round 4
   * (design.md section 5, 2026-09-26). Opening the chat reads them, so on the chat itself there is
   * none. A count that could not be read shows nothing rather than a zero or a guess.
   */
  const onChat = location.pathname === "/chat";
  const chatSummary = useUnreadChatSummary(identity.data?.tenantId ?? null, identity.data?.userId ?? null);
  const chatUnread = onChat ? 0 : (chatSummary.data?.unreadCount ?? 0);
  const chatCount = (to: string) =>
    to === "/chat" && chatUnread > 0 ? (
      <span className="count nav-count" data-testid="chat-unread-count" aria-hidden="true">
        {chatUnread > 99 ? "99+" : chatUnread}
      </span>
    ) : null;
  const navLabel = (to: string, label: string) =>
    to === "/chat" && chatUnread > 0 ? `${label}, ${chatUnread} ${chatUnread === 1 ? "oläst" : "olästa"}` : undefined;

  // The chat follows the part of the screen a phone's keyboard leaves (build round 5, 2026-09-27).
  const shell = useRef<HTMLDivElement | null>(null);
  const keyboardOpen = useKeyboardViewport(onChat, shell);

  // Webbplatsens fakta, on a successful non-empty answer only (build round 19, 2026-10-08).
  const factsSites = useClientSiteFactsSites(identity.data?.tenantId ?? null);
  const hasFacts = factsSites.isSuccess && factsSites.data.length > 0;
  const factsRail = hasFacts ? withFacts(DESTINATIONS, "/documents") : DESTINATIONS;
  const factsMore = hasFacts ? withFacts(MOBILE_SECONDARY, null) : MOBILE_SECONDARY;
  // Statistik, on a successful non-empty answer only (build round 37, 2026-10-11), after Webbplatsens
  // fakta when it is shown, so the facts keep their place (right after Dokument, first in Mer).
  const siteCounts = useClientSiteCounts(identity.data?.tenantId ?? null);
  const hasStatistics = siteCounts.isSuccess && siteCounts.data.length > 0;
  const railItems = hasStatistics ? withStatistics(factsRail, hasFacts ? "/site-facts" : "/documents") : factsRail;
  const moreItems = hasStatistics ? withStatistics(factsMore, hasFacts ? "/site-facts" : null) : factsMore;

  if (belongsOnAdminSurface(identity.data, location.pathname)) {
    return <Navigate to={ADMIN_HOME} replace />;
  }

  return (
    <div
      ref={shell}
      className={`portal-shell${onChat ? " is-chat" : ""}${onChat && keyboardOpen ? " is-keyboard" : ""}`}
    >
      <a className="skip-link" href="#main-content">
        Hoppa till huvudinnehållet
      </a>

      <aside className="portal-rail">
        <div className="portal-brand">
          <span className="wordmark">PETROVIC SYSTEMS</span>
          <small>Kundportal</small>
        </div>

        <nav className="rail-nav" aria-label="Huvudnavigation">
          {railItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}`}
              aria-label={navLabel(to, label)}
            >
              <Icon size={19} weight="regular" aria-hidden="true" />
              <span>{label}</span>
              {chatCount(to)}
            </NavLink>
          ))}
        </nav>

        {/*
          No initials circle here. It restated the first letters of the name printed 10 px to its
          right, was `aria-hidden` so it said nothing to a reader either, and the 46 px it took were
          the 46 px the name needed: the text column was 99 px and Geist draws "Sanel Kadunic" at
          113 px, so a real client read their own name truncated on every screen. Initials still
          earn their place in chat and ärenden, where they separate several speakers in a list.
        */}
        <div className="rail-foot">
          <span className="rail-identity">
            {/*
              Three states, three sentences. "Laddar" is true only while it is true; a profile with
              no name says so rather than borrowing the loading text, which is what it used to do and
              what a real client saw for the whole of their first session.
            */}
            <strong data-name-state={person.kind}>
              {person.kind === "loading"
                ? "Laddar"
                : person.kind === "named"
                  ? person.display
                  : "Namn saknas"}
            </strong>
            <small>{identity.data?.tenantDisplayName ?? ""}</small>
          </span>
          <button
            type="button"
            className="icon-button"
            onClick={() => void signOut()}
            aria-label="Logga ut"
          >
            <SignOut size={18} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <header className="portal-header">
        <p className="mobile-wordmark">
          PETROVIC SYSTEMS <span>Kundportal</span>
        </p>
        <div className="header-actions">
          <NavLink
            to="/notifications"
            className="icon-button notification-button"
            aria-label={notificationLabel(unread)}
          >
            <Bell size={20} aria-hidden="true" />
            {unread !== null && unread > 0 ? (
              <span className="notification-dot" data-testid="unread-dot" />
            ) : null}
          </NavLink>
        </div>
      </header>

      {/*
        `tabIndex={0}` rather than `-1`, because this element is now a scroll container.

        It was `-1` for as long as the document did the scrolling: focusable programmatically, so
        the skip link and `RouteChrome` could move focus here, and out of the tab order because
        there was nothing to do once you arrived. Putting the overflow on `.portal-main` changed
        that. A region that scrolls and cannot be focused cannot be scrolled from the keyboard at
        all, which is WCAG 2.1.1, and axe reports it as `scrollable-region-focusable`. It surfaced
        on the documents page at 844 x 390, where the empty state puts no focusable element inside
        the region at all, so there was no other way in.

        The cost is one tab stop at the top of the page. Programmatic focus is unaffected.
      */}
      <main className="portal-main" id="main-content" tabIndex={0}>
        {/*
          The reconnect state plan.md section 5.8 asks for, above the page rather than inside it, so
          every one of the seven destinations gets it without each of them carrying its own copy.
        */}
        <ConnectionNotice />
        {/*
          Above the routed content and inside the authenticated layout, so a waiting version is
          offered on every screen a client can be on and never on the sign-in page, where a reload
          would throw away a half-typed password. Below `ConnectionNotice`, because a lost
          connection is about what is happening now and a new version can wait.
        */}
        <UpdatePrompt />
        <Outlet />
      </main>

      <nav className="mobile-nav" aria-label="Huvudnavigation, mobil">
        {MOBILE_PRIMARY.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
            aria-label={navLabel(to, label)}
          >
            <span className="mobile-nav-icon">
              <Icon size={20} aria-hidden="true" />
              {chatCount(to)}
            </span>
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`mobile-nav-item${moreOpen ? " is-active" : ""}`}
          aria-expanded={moreOpen}
          aria-controls="mobile-more"
          ref={moreButton}
          onClick={() => setMoreOpen((open) => !open)}
        >
          <DotsThreeCircle size={20} aria-hidden="true" />
          <span>Mer</span>
        </button>
      </nav>

      {moreOpen ? (
        <nav className="mobile-more" id="mobile-more" aria-labelledby="mobile-more-title" ref={moreSheet}>
          <header className="mobile-more-head">
            <h2 id="mobile-more-title">Mer</h2>
            <button
              type="button"
              className="icon-button"
              aria-label="Stäng"
              onClick={() => {
                setMoreOpen(false);
                moreButton.current?.focus();
              }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </header>
          {moreItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className="mobile-more-item"
              // Closed by the navigation itself rather than by an effect watching the path: the
              // sheet is dismissed because someone chose a destination, which is the actual cause.
              onClick={() => setMoreOpen(false)}
            >
              <span className="type-icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <span className="mobile-more-label">{label}</span>
              <CaretRight size={16} aria-hidden="true" className="mobile-more-go" />
            </NavLink>
          ))}
          {/*
            The only sign-out a phone has, added 2026-09-18. Under 780 px the rail above is
            `display: none`, and its foot held the one client sign-out in the portal, so a client on
            a phone could not sign out at all: measured on Mattej's iPhone on 2026-09-17, and a
            failure of security checklist S6, "invalidated on logout". It sits in this sheet because
            the sheet is where the rail's other contents already go on a phone, and it calls the same
            `signOut` with the same default, `local`, so pressing it ends this phone's session and no
            other. Separated from the destinations by a rule, because it is an act and not a place.
          */}
          <button
            type="button"
            className="mobile-more-item mobile-more-signout"
            onClick={() => {
              setMoreOpen(false);
              void signOut();
            }}
          >
            <SignOut size={20} aria-hidden="true" />
            <span>Logga ut</span>
          </button>
        </nav>
      ) : null}

      {openingDone ? null : <PortalOpening onDone={() => setOpeningDone(true)} />}
    </div>
  );
}
